import React from 'react';
import InputLabel from 'components/InputLabel';
import useInput from 'hooks/useInput';
import ErrorMessage from './ErrorMessage';
import OptionalAdornment from './OptionalAdornment/OptionalAdornment';
import * as S from './styles';
import { TextFieldProps } from './types';

/**
 * An accessible text input field.
 */
export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      borderless,
      className,
      endAdornment = null,
      fluid,
      onBlur,
      onFocus,
      onFocusChange,
      onChange,
      optional,
      startAdornment = null,
      ...textFieldProps
    },
    inputRef,
  ) => {
    const { errorMessage, isDisabled, label, validationState } = textFieldProps;

    const {
      errorMessageProps,
      focused,
      inputProps,
      labelProps,
      onContainerClick: handleContainerClick,
      onContainerFocus: handleContainerFocus,
      onContainerMouseDown: handleContainerMouseDown,
      ref,
    } = useInput({
      inputRef,
      onBlur,
      onChange,
      onFocus,
      onFocusChange,
      textFieldProps,
    });

    const effectiveEndAdornment = optional ? (
      <OptionalAdornment />
    ) : (
      endAdornment
    );
    const hasEndAdornment = !!effectiveEndAdornment;
    const hasStartAdornment = !!startAdornment;

    return (
      <S.Root {...{ hasEndAdornment, hasStartAdornment, isDisabled, fluid }}>
        {label && <InputLabel {...labelProps}>{label}</InputLabel>}
        <S.InputContainer
          borderless={borderless}
          className={className}
          data-focused={focused}
          endAdornment={effectiveEndAdornment}
          inputComponent="input"
          inputComponentProps={{
            ...inputProps,
            ref,
          }}
          isDisabled={isDisabled}
          onClick={handleContainerClick}
          onFocus={handleContainerFocus}
          onMouseDown={handleContainerMouseDown}
          startAdornment={startAdornment}
          validationState={validationState}
        />
        {errorMessage !== undefined && (
          <ErrorMessage {...errorMessageProps}>{errorMessage}</ErrorMessage>
        )}
      </S.Root>
    );
  },
);

export default TextField;
